/** OS 폴더/파일 드래그앤드롭용 File System Entry 헬퍼 */

export type UploadNode =
  | { type: 'file'; name: string; file: File }
  | { type: 'dir'; name: string; children: UploadNode[] };

interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FileSystemFileEntryLike extends FileSystemEntryLike {
  file(
    successCallback: (file: File) => void,
    errorCallback?: (err: DOMException) => void
  ): void;
}

interface FileSystemDirectoryReaderLike {
  readEntries(
    successCallback: (entries: FileSystemEntryLike[]) => void,
    errorCallback?: (err: DOMException) => void
  ): void;
}

interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  createReader(): FileSystemDirectoryReaderLike;
}

function asEntry(item: DataTransferItem): FileSystemEntryLike | null {
  const anyItem = item as DataTransferItem & {
    webkitGetAsEntry?: () => FileSystemEntryLike | null;
  };
  if (typeof anyItem.webkitGetAsEntry === 'function') {
    return anyItem.webkitGetAsEntry();
  }
  return null;
}

function entryToFile(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
}

async function readAllDirectoryEntries(
  dir: FileSystemDirectoryEntryLike
): Promise<FileSystemEntryLike[]> {
  const reader = dir.createReader();
  const all: FileSystemEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FileSystemEntryLike[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    if (batch.length === 0) break;
    all.push(...batch);
  }
  return all;
}

async function buildTreeFromEntry(entry: FileSystemEntryLike): Promise<UploadNode | null> {
  if (entry.name === '.' || entry.name === '..' || entry.name.startsWith('.')) {
    return null;
  }
  if (entry.isFile) {
    const file = await entryToFile(entry as FileSystemFileEntryLike);
    return { type: 'file', name: entry.name, file };
  }
  if (entry.isDirectory) {
    const childrenEntries = await readAllDirectoryEntries(
      entry as FileSystemDirectoryEntryLike
    );
    const children: UploadNode[] = [];
    for (const child of childrenEntries) {
      const node = await buildTreeFromEntry(child);
      if (node) children.push(node);
    }
    return { type: 'dir', name: entry.name, children };
  }
  return null;
}

/** DataTransfer에서 폴더 트리 + 개별 파일 노드 수집 */
export async function collectUploadNodesFromDataTransfer(
  dataTransfer: DataTransfer
): Promise<UploadNode[]> {
  const items = Array.from(dataTransfer.items || []);
  if (items.length > 0) {
    const nodes: UploadNode[] = [];
    for (const item of items) {
      if (item.kind !== 'file') continue;
      const entry = asEntry(item);
      if (entry) {
        const node = await buildTreeFromEntry(entry);
        if (node) nodes.push(node);
        continue;
      }
      const file = item.getAsFile();
      if (file) nodes.push({ type: 'file', name: file.name, file });
    }
    if (nodes.length > 0) return nodes;
  }

  return Array.from(dataTransfer.files || []).map((file) => ({
    type: 'file' as const,
    name: file.name,
    file,
  }));
}

/** input[webkitdirectory] FileList → 상대경로(webkitRelativePath) 기준 트리 */
export function collectUploadNodesFromFileList(files: FileList | File[]): UploadNode[] {
  const list = Array.from(files);
  if (list.length === 0) return [];

  type MutableDir = { type: 'dir'; name: string; children: UploadNode[]; map: Map<string, MutableDir> };
  const rootChildren: UploadNode[] = [];
  const rootDirs = new Map<string, MutableDir>();

  const ensureDir = (
    map: Map<string, MutableDir>,
    siblings: UploadNode[],
    name: string
  ): MutableDir => {
    let dir = map.get(name);
    if (!dir) {
      dir = { type: 'dir', name, children: [], map: new Map() };
      map.set(name, dir);
      siblings.push(dir);
    }
    return dir;
  };

  for (const file of list) {
    const rel =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath?.trim() ||
      file.name;
    const parts = rel.split('/').filter(Boolean);
    if (parts.length === 0) continue;
    if (parts.some((p) => p.startsWith('.'))) continue;

    if (parts.length === 1) {
      rootChildren.push({ type: 'file', name: parts[0], file });
      continue;
    }

    let map = rootDirs;
    let siblings = rootChildren;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const dir = ensureDir(map, siblings, parts[i]);
      map = dir.map;
      siblings = dir.children;
    }
    siblings.push({ type: 'file', name: parts[parts.length - 1], file });
  }

  const strip = (nodes: UploadNode[]): UploadNode[] =>
    nodes.map((node) =>
      node.type === 'file'
        ? node
        : {
            type: 'dir',
            name: node.name,
            children: strip(
              (node as MutableDir).children
            ),
          }
    );

  return strip(rootChildren);
}

export function countUploadNodes(nodes: UploadNode[]): { folders: number; files: number } {
  let folders = 0;
  let files = 0;
  const walk = (list: UploadNode[]) => {
    for (const node of list) {
      if (node.type === 'file') files += 1;
      else {
        folders += 1;
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return { folders, files };
}
